import React from "react";
import { Box, Button, Chip, MenuItem, TextField } from "@mui/material";

import tokens from "../../theme/tokens";

const { color, font } = tokens;

/** Options for the Company dropdown, shared by All Jobs and All Users.
 *
 *  `selected` is folded in as a placeholder option when the companies haven't arrived yet: a
 *  shared link can carry `?company_id=<uuid>` on first paint, and MUI renders a Select whose
 *  value matches no MenuItem as blank — with an out-of-range console warning. The placeholder
 *  is replaced by the real name as soon as getAdminCompanies() resolves. */
export const companyOptions = (companies, selected) => {
  const options = [
    { value: "", label: "All companies" },
    { value: "none", label: "No company" },
    ...companies.map((c) => ({ value: c.id, label: c.name || c.email_domain })),
  ];
  if (selected && !options.some((option) => option.value === selected)) {
    options.push({ value: selected, label: "…" });
  }
  return options;
};

/** Search box + dropdowns + removable chips for the admin tables. Presentational: it owns
 *  no state, and every change is handed straight back to useAdminFilters. */
const AdminFilterBar = ({
  searchPlaceholder,
  searchValue,
  onSearchChange,
  dropdowns = [],   // [{ key, label, value, options: [{ value, label }] }]
  chips = [],       // [{ key, label, onDelete }]
  hasFilters,
  onClear,
}) => (
  <Box sx={{ mb: 2 }}>
    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
      <TextField
        size="small"
        placeholder={searchPlaceholder}
        value={searchValue}
        onChange={(e) => onSearchChange(e.target.value)}
        sx={{ minWidth: 260, bgcolor: color.surface }}
      />

      {dropdowns.map((dropdown) => (
        <TextField
          key={dropdown.key}
          select
          size="small"
          label={dropdown.label}
          value={dropdown.value}
          onChange={(e) => dropdown.onChange(e.target.value)}
          sx={{ minWidth: 170, bgcolor: color.surface }}
        >
          {dropdown.options.map((option) => (
            <MenuItem key={option.value || "all"} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      ))}

      {hasFilters && (
        <Button size="small" onClick={onClear} sx={{ font: `500 12.5px ${font.sans}` }}>
          Clear filters
        </Button>
      )}
    </Box>

    {chips.length > 0 && (
      <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
        {chips.map((chip) => (
          <Chip key={chip.key} size="small" label={chip.label} onDelete={chip.onDelete} />
        ))}
      </Box>
    )}
  </Box>
);

export default AdminFilterBar;
